import './DashboardPage.scss';

import { useGetDashboardChartMutation, useGetDashboardDataMutation } from '@services/product/dashboard/dashboard.endpoints';
import { DashboardChart, DashboardData, SeriesType } from '@services/product/dashboard/dashboard.types';
import { ApexOptions } from 'apexcharts';
import { useEffect, useState } from 'react';
import { formatMoney } from '@utils/numbers';
import { useNavigate } from 'react-router-dom';

export default function useDashboardPageVM() {
    const navigate = useNavigate();

    const [getDashboardData, { data: dashboardDataResponse, isLoading: dashboardDataLoading, isUninitialized: dashboardDataUninitialized }] = useGetDashboardDataMutation();
    const [dashboardData, setDashboardData] = useState<DashboardData>();
    const [dashboardChart, setDashboardChart] = useState<DashboardChart>();
    const [getDashboardChart, { data: dashboardChartResponse, isLoading: dashboardChartLoading, isUninitialized: dashboardChartUninitialized }] = useGetDashboardChartMutation();
    const [series, setSeries] = useState<SeriesType>([]);

    const [startDate, setStartDate] = useState(new Date(new Date().getFullYear(), 0, 1)); //start of year automatically set
    const [endDate, setEndDate] = useState(new Date(new Date().getFullYear(), 11, 31)); //end of year automatically set

    const [options, setOptions] = useState<ApexOptions>({
        chart: {
            type: 'area',
            toolbar: {
                show: false
            },
            selection: {
                enabled: false
            },
            zoom: {
                enabled: false
            },
            defaultLocale: 'fr',
            locales: [
                {
                    name: 'fr',
                    options: {
                        months: [
                            'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
                            'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
                        ],
                        shortMonths: [
                            'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin',
                            'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'
                        ],
                        days: [
                            'Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'
                        ],
                        shortDays: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam']
                    }
                }
            ]
        },
        markers: {
            size: 4,
            colors: ["#ffffff"],
            strokeColors: ["var(--primary)"],
            strokeWidth: 1,
            hover: {
              size: 6
            }
        },
        dataLabels: {
            enabled: false
        },
        stroke: {
            curve: 'smooth',
            colors: ['var(--primary)'],
            width: 2
        },
        xaxis: {
            type: 'datetime',
            categories: []
        },
        yaxis: {
            labels: {
                formatter: (value: number) => {
                    return formatMoney(value);
                }
            }
        },
        tooltip: {
            x: {
                format: 'MMMM yyyy',
            },
            y: {
                formatter: (value: number) => {
                    return formatMoney(value);
                }
            }
        },
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.7,
                opacityTo: 0.9,
                stops: [0, 100],
                colorStops: [
                    {
                        offset: 0,
                        color: 'var(--primary)',
                        opacity: 0.4
                    },
                    {
                        offset: 100,
                        color: '#ffffff',
                        opacity: 0.2
                    }
                ]
            }
        },
        grid: {
            show: true,
            xaxis: {
                lines: {
                    show: true,
                }
            },
            yaxis: {
                lines: {
                    show: true
                }
            }
        }
    });


    useEffect(() => {
        getDashboardData(undefined);
    }, []);

    useEffect(() => {
        getDashboardChart({ startDate: startDate.toISOString().split('T')[0], endDate: endDate.toISOString().split('T')[0] });
    }, [startDate, endDate]);

    useEffect(() => {
        if (dashboardDataResponse) {
            setDashboardData(dashboardDataResponse.data);
        }
    }, [dashboardDataResponse]);



    useEffect(() => {
        if (dashboardChartResponse) {
            setDashboardChart(dashboardChartResponse.data);
            setSeries(
                [
                    {
                        name: 'Chiffre d\'affaires',
                        data: dashboardChartResponse.data.serie.data
                    }
                ]
            );
            setOptions((prev) => {
                return {
                    ...prev,
                    xaxis: {
                        type: 'datetime',
                        categories: dashboardChartResponse.data.serie.category
                    }
                }
            });
        }
    }, [dashboardChartResponse]);

    return {
        dashboardDataIsLoading: dashboardDataLoading || dashboardDataUninitialized,
        dashboardData,
        chartIsLoading: dashboardChartLoading || dashboardChartUninitialized,
        series,
        options,
        dashboardChart,
        startDate,
        endDate,
        setStartDate,
        setEndDate,
        navigate
    }
};
